import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TeamSignatureAPI, type TeamSignature } from '../../../lib/apiTeamSignature';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import { AccountSection } from './AccountSection';
import { getApiErrorMessage } from '../../../lib/apiError';

function Editor({ saved }: { saved: TeamSignature }) {
  const cache = useQueryClient();
  const [baseline, setBaseline] = useState(saved);
  const [template, setTemplate] = useState(saved.template);
  const [enabled, setEnabled] = useState(saved.enabled);
  const [notice, setNotice] = useState('');
  const save = useMutation({
    mutationFn: () => TeamSignatureAPI.save({ template, enabled, revision: baseline.revision }),
    onSuccess: (data) => {
      setBaseline(data);
      cache.setQueryData(['admin', 'team-signature'], data);
      setNotice('Team signature saved.');
    },
  });
  const dirty = template !== baseline.template || enabled !== baseline.enabled;
  const blocker = useBlocker(dirty || save.isPending);
  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (dirty || save.isPending) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      [dirty, save.isPending],
    ),
  );
  const preview = template
    .replace(
      /\{\{\s*([a-z_]+)\s*\}\}/g,
      (code, key: string) => saved.sender[key as keyof typeof saved.sender] ?? code,
    )
    .split('\n')
    .filter((line) => line.trim())
    .join('\n');
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Typography level="h3">Team signature</Typography>
      <Typography>
        One signature for emails sent from Sales and Accounts. Each teammate’s name, sending email address,
        and profile phone number are filled in automatically. Automated onboarding emails keep their own
        configured sender and content.
      </Typography>
      <AccountSection
        title="Signature layout & preview"
        description="Edit the shared layout on the left and check how it looks with your details on the right. Save to apply it for the team."
        help="Variables use each sender’s details, not the customer’s. The email address comes from their sending mailbox; the phone number comes from Profile settings."
      >
        <Checkbox
          label="Automatically add the team signature"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          disabled={save.isPending}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          <FormControl>
            <FormLabel>Signature layout</FormLabel>
            <Textarea
              minRows={8}
              value={template}
              onChange={(e) => {
                setTemplate(e.target.value);
                setNotice('');
              }}
              disabled={save.isPending}
            />
            <Typography level="body-xs" sx={{ mt: 1 }}>
              Use {'{{name}}'}, {'{{first_name}}'}, {'{{last_name}}'}, {'{{email}}'}, or {'{{phone}}'}{' '}
              anywhere. Add shared company details as ordinary text. Empty lines are removed.
            </Typography>
          </FormControl>
          <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg' }}>
            <Typography level="title-sm">Preview with your details</Typography>
            <Typography level="body-xs" sx={{ mt: 0.5 }}>
              This is how the sign-off will appear below your message.
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', mt: 2, overflowWrap: 'anywhere' }}>
              {preview}
            </Typography>
            {!saved.sender.phone && (
              <Typography level="body-xs" sx={{ mt: 2 }}>
                Add your phone in Profile settings to include it.
              </Typography>
            )}
          </Sheet>
        </Box>
        {save.error && (
          <Alert color="danger">{getApiErrorMessage(save.error, 'Could not save the signature.')}</Alert>
        )}
        {notice && <Alert color="success">{notice}</Alert>}
        <Button
          sx={{ justifySelf: 'start' }}
          loading={save.isPending}
          disabled={!dirty}
          onClick={() => save.mutate()}
        >
          Save team signature
        </Button>
      </AccountSection>
      <Modal
        open={blocker.state === 'blocked'}
        onClose={() => blocker.state === 'blocked' && blocker.reset()}
      >
        <ModalDialog role="alertdialog" aria-labelledby="signature-leave-title">
          <Typography id="signature-leave-title" level="h4">
            Keep your signature changes?
          </Typography>
          <Typography>Your changes have not been saved.</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => blocker.state === 'blocked' && blocker.reset()}>Keep editing</Button>
            <Button
              color="danger"
              variant="outlined"
              disabled={save.isPending}
              onClick={() => blocker.state === 'blocked' && blocker.proceed()}
            >
              Discard changes
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
export function TeamSignaturePanel() {
  const query = useQuery({
    queryKey: ['admin', 'team-signature'],
    queryFn: TeamSignatureAPI.get,
    refetchOnWindowFocus: false,
  });
  if (query.isPending) return <Typography role="status">Loading team signature…</Typography>;
  if (!query.data || query.isError)
    return (
      <Alert color="danger" endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}>
        Could not load the signature.
      </Alert>
    );
  return <Editor saved={query.data} />;
}
export function TeamSignaturePreview() {
  const query = useQuery({
    queryKey: ['admin', 'team-signature'],
    queryFn: TeamSignatureAPI.get,
  });
  if (!query.data?.enabled) return null;
  return (
    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'md' }}>
      <Typography level="body-xs">Team signature · added automatically when sending</Typography>
      <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
        {query.data.preview}
      </Typography>
    </Sheet>
  );
}
